import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, AlertTriangle, CheckCircle2, Inbox, RefreshCw } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { inventaireApi, type EcartInventaire, type StatutEcart } from '@/api/inventaire';
import { toast } from '@/stores/toastStore';

/**
 * Écarts d'inventaire physique — 29/09/2026.
 *
 * Avant cet écran, un écart n'existait que dans la réponse HTTP de la validation
 * et dans les journaux du serveur. Un comptage de 4 poteaux face à 0 au parc
 * s'est perdu huit jours : personne ne pouvait dire ce qui restait à trancher.
 *
 * Les quantités affichées sont celles du JOUR DU COMPTAGE, pas d'aujourd'hui.
 * L'écran ne recalcule rien — un constat qui bouge tout seul ne peut pas être
 * tranché.
 */

const LIBELLE_STATUT: Record<StatutEcart, string> = {
  corrige:             'Redressé',
  partiel:             'Partiellement redressé',
  non_corrige:         'À trancher',
  resolu_manuellement: 'Tranché',
};

const COULEUR_STATUT: Record<StatutEcart, string> = {
  corrige:             'ds-bd ds-bd-s',
  partiel:             'ds-bd ds-bd-w',
  non_corrige:         'ds-bd ds-bd-d',
  resolu_manuellement: 'ds-bd ds-bd-n',
};

const nombre = (v: string) =>
  Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 3 });

const signe = (v: string) => (Number(v) > 0 ? `+${nombre(v)}` : nombre(v));

export const EcartsInventairePage = () => {
  const [filtre, setFiltre] = useState<'ouverts' | 'tous'>('ouverts');
  const [aTrancher, setATrancher] = useState<EcartInventaire | null>(null);
  const [note, setNote] = useState('');
  const qc = useQueryClient();

  const ecartsQ = useQuery({
    queryKey: ['inventaire', 'ecarts', filtre],
    queryFn:  () => inventaireApi.ecarts(filtre === 'ouverts' ? { statut: 'ouverts' } : undefined),
    staleTime: 30_000,
  });

  const resoudre = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => inventaireApi.resoudre(id, note),
    onSuccess: () => {
      toast.success('Écart tranché', 'La décision est consignée au constat.');
      qc.invalidateQueries({ queryKey: ['inventaire', 'ecarts'] });
      setATrancher(null);
      setNote('');
    },
    onError: () => toast.error('Échec', "L'écart n'a pas pu être clos."),
  });

  const lignes  = ecartsQ.data?.data ?? [];
  const ouverts = ecartsQ.data?.resume.ouverts ?? 0;

  return (
    <>
      <Topbar
        title="Écarts d'inventaire"
        subtitle={
          ouverts > 0
            ? `${ouverts} écart${ouverts > 1 ? 's' : ''} attend${ouverts > 1 ? 'ent' : ''} une décision`
            : 'Aucun écart en attente de décision'
        }
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => ecartsQ.refetch()}>
            <RefreshCw size={14} /> Rafraîchir
          </button>
        }
      />

      <div style={{ padding: '16px 20px 40px' }}>
        {/* Ce que l'écran montre, et ce qu'il ne fait pas — un écart de stock se
            lit mal sans savoir de quand il date. */}
        <div className="ds-alert-strip ds-alert-i" style={{ marginBottom: 14 }}>
          <span className="ds-alert-ico"><ClipboardCheck size={20} /></span>
          <div className="ds-alert-txt">
            Chaque ligne compare ce que le terrain a compté à ce que le système portait
            <strong> le jour du comptage</strong>. Ces quantités sont figées : elles ne suivent pas
            les mouvements de stock survenus depuis, sinon un constat changerait tout seul et ne
            pourrait plus être tranché.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {(['ouverts', 'tous'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFiltre(f)}
              className={`ds-btn ds-btn-sm ${filtre === f ? 'ds-btn-p' : 'ds-btn-g'}`}
            >
              {f === 'ouverts' ? 'À trancher' : 'Tout l’historique'}
            </button>
          ))}
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Constats</div>
              <div className="ds-table-sub">
                {lignes.length} constat{lignes.length > 1 ? 's' : ''} — du plus récent au plus ancien
              </div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Comptage</th>
                <th style={{ width: 150 }}>Article</th>
                <th style={{ width: 100 }}>Compté</th>
                <th style={{ width: 110 }}>Au système</th>
                <th style={{ width: 90 }}>Écart</th>
                <th style={{ width: 110 }}>Redressé</th>
                <th>Suite donnée</th>
                <th style={{ width: 130 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {ecartsQ.isLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>
              )}
              {!ecartsQ.isLoading && lignes.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  {filtre === 'ouverts' ? (
                    <>
                      <CheckCircle2 size={28} style={{ margin: '0 auto 8px', color: 'var(--success)' }} />
                      <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                        Aucun écart en attente. Les comptages validés sont soit conformes, soit redressés.
                      </p>
                    </>
                  ) : (
                    <>
                      <Inbox size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                      <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                        Aucun comptage physique n’a encore produit d’écart.
                      </p>
                    </>
                  )}
                </td></tr>
              )}
              {!ecartsQ.isLoading && lignes.map((e) => {
                const manquant = Number(e.ecart) < 0;
                return (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{e.dateComptage.slice(0, 10)}</td>
                    <td>
                      <span className="ds-ref-code">{e.articleCode}</span>
                      <div style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 3 }}>
                        {e.articleType === 'matiere_premiere' ? 'Matière première' : 'Produit fini'}
                      </div>
                    </td>
                    <td className="ds-am">{nombre(e.quantiteComptee)} {e.unite}</td>
                    <td className="ds-am">{nombre(e.quantiteSysteme)} {e.unite}</td>
                    <td className="ds-am" style={{ fontWeight: 700, color: manquant ? 'var(--danger)' : 'var(--warning)' }}>
                      {signe(e.ecart)}
                    </td>
                    <td className="ds-am">
                      {Number(e.quantiteCorrigee) === 0 ? '—' : `${signe(e.quantiteCorrigee)} ${e.unite}`}
                    </td>
                    <td>
                      <span className={COULEUR_STATUT[e.statut]} style={{ fontSize: 10 }}>
                        {LIBELLE_STATUT[e.statut]}
                      </span>
                      {e.motif && (
                        <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4, lineHeight: 1.45 }}>
                          {e.motif}
                        </div>
                      )}
                      {e.resolutionNote && (
                        <div style={{ fontSize: 11, color: 'var(--tx-2)', marginTop: 4, lineHeight: 1.45 }}>
                          <strong>Décision :</strong> {e.resolutionNote}
                          {e.resoluAt && (
                            <span style={{ color: 'var(--tx-3)' }}> · {e.resoluAt.slice(0, 10)}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {e.statut === 'non_corrige' || e.statut === 'partiel' ? (
                        <button
                          className="ds-btn ds-btn-p ds-btn-sm"
                          onClick={() => { setATrancher(e); setNote(''); }}
                        >
                          Trancher
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {aTrancher && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 60,
          }}
          onClick={() => setATrancher(null)}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            style={{
              background: 'var(--bg-0, #fff)', borderRadius: 12, padding: 20,
              width: '100%', maxWidth: 520, boxShadow: '0 20px 50px rgba(0,0,0,.25)',
            }}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>
              Trancher l’écart sur {aTrancher.articleCode}
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.5 }}>
              Comptage du {aTrancher.dateComptage.slice(0, 10)} :{' '}
              <strong>{nombre(aTrancher.quantiteComptee)} {aTrancher.unite}</strong> comptés contre{' '}
              <strong>{nombre(aTrancher.quantiteSysteme)}</strong> au système, soit{' '}
              <strong>{signe(aTrancher.ecart)}</strong>.
            </p>

            <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 14 }}>
              <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
              <div className="ds-alert-txt" style={{ fontSize: 12 }}>
                Trancher <strong>ne modifie aucun stock</strong>. Cela consigne votre décision et
                retire le constat de la liste d’attente. Si le stock doit bouger, faites-le par le
                geste métier qui correspond — une déclaration de production, un placement au parc,
                une sortie.
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
              Ce que vous avez constaté ou décidé
            </label>
            <textarea
              value={note}
              onChange={(ev) => setNote(ev.target.value)}
              rows={4}
              placeholder="Ex. : 4 poteaux retrouvés hors zone, déclaration de production du 21/09 à saisir par le chef de parc."
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)',
                borderRadius: 8, fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
              }}
            />
            <p style={{ margin: '6px 0 16px', fontSize: 11, color: 'var(--tx-3)' }}>
              Au moins 5 caractères — un écart clos sans explication est un écart perdu une
              seconde fois.
            </p>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ds-btn ds-btn-g" onClick={() => setATrancher(null)}>Annuler</button>
              <button
                className="ds-btn ds-btn-p"
                disabled={note.trim().length < 5 || resoudre.isPending}
                onClick={() => resoudre.mutate({ id: aTrancher.id, note: note.trim() })}
              >
                {resoudre.isPending ? 'Enregistrement…' : 'Consigner la décision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
